import React, { useEffect, useCallback, useRef, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  CircularProgress,
  TextField,
  InputAdornment,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import apiClient from '../api/client';
import { deleteRun } from '../api/runs';
import { Run } from '../types';
import { getGpuHourlyRate } from '../constants/training';
import { useWebSocket } from '../hooks/useWebSocket';
import { relativeTime, getStatusColor } from '../utils/artifacts';

const pulseKeyframes = `
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
`;

if (typeof document !== 'undefined') {
  const style = document.createElement('style');
  style.textContent = pulseKeyframes;
  document.head.appendChild(style);
}

type SortField = 'name' | 'status' | 'duration' | 'cost' | 'created_at';
type SortDir = 'asc' | 'desc';

const STATUS_ORDER: Record<string, number> = { running: 0, queued: 1, completed: 2, failed: 3 };

const STATUS_FILTERS = ['all', 'running', 'queued', 'completed', 'failed'] as const;

const getEnvLabel = (run: Run) => {
  return run.config?.training_config?.task_name || '';
};

const getDurationMs = (run: Run) => {
  const start = new Date(run.created_at).getTime();
  const end = (run.status === 'completed' || run.status === 'failed' || run.status === 'paused')
    ? new Date(run.updated_at).getTime()
    : Date.now();
  return end - start;
};

const formatDuration = (ms: number) => {
  const h = ms / 3600000;
  if (h >= 1) return `${h.toFixed(1)}h`;
  const m = Math.round(h * 60);
  return m > 0 ? `${m}m` : '<1m';
};

const getEstCost = (run: Run) => {
  const gpuCount = run.config?.training_config?.gpu_count ?? 1;
  const gpuType = run.config?.fleet?.gpu_type ?? 'RTX 4090';
  const maxWorkers = run.config?.fleet?.max_workers ?? 1;
  const baseRate = getGpuHourlyRate(gpuType);
  const hourlyRate = gpuCount * baseRate * maxWorkers;
  const durationHrs = getDurationMs(run) / 3600000;
  return durationHrs * hourlyRate;
};

const RunsList: React.FC = () => {
  const [runs, setRuns] = useState<Run[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [deleting, setDeleting] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Run | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const navigate = useNavigate();

  const handleDeleteClick = (run: Run, e: React.MouseEvent) => {
    e.stopPropagation();
    if (deleting) return;
    setDeleteTarget(run);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setDeleteTarget(null);
    setDeleteConfirmText('');
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(deleteTarget.slug);
    setDeleteDialogOpen(false);
    try {
      await deleteRun(deleteTarget.slug);
      setRuns((prev) => prev.filter((r) => r.slug !== deleteTarget.slug));
    } catch (err) {
      console.error('Failed to delete run:', err);
    } finally {
      setDeleting(null);
      setDeleteTarget(null);
      setDeleteConfirmText('');
    }
  };

  const loadRuns = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/v1/runs');
      setRuns(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load runs:', error);
      setRuns([]);
      setLoading(false);
    }
  }, []);

  const runsRef = useRef(runs);
  runsRef.current = runs;

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  // Poll every 10s when there are active (running/queued) runs
  useEffect(() => {
    const interval = setInterval(() => {
      const hasActive = runsRef.current.some(
        (r) => r.status === 'running' || r.status === 'queued',
      );
      if (hasActive) loadRuns();
    }, 10000);
    return () => clearInterval(interval);
  }, [loadRuns]);

  useWebSocket((message) => {
    if (message.type === 'run_update' || message.type === 'task_update') {
      loadRuns();
    }
  });

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'created_at' ? 'desc' : 'asc');
    }
  };

  const filtered = useMemo(() => {
    let result = runs;

    if (statusFilter !== 'all') {
      result = result.filter((r) => r.status === statusFilter);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          getEnvLabel(r).toLowerCase().includes(q),
      );
    }

    return [...result].sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      switch (sortField) {
        case 'name':
          return dir * a.name.localeCompare(b.name);
        case 'status':
          return dir * ((STATUS_ORDER[a.status] ?? 9) - (STATUS_ORDER[b.status] ?? 9));
        case 'duration':
          return dir * (getDurationMs(a) - getDurationMs(b));
        case 'cost':
          return dir * (getEstCost(a) - getEstCost(b));
        case 'created_at':
          return dir * (new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
        default:
          return 0;
      }
    });
  }, [runs, search, statusFilter, sortField, sortDir]);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: runs.length };
    for (const r of runs) {
      counts[r.status] = (counts[r.status] || 0) + 1;
    }
    return counts;
  }, [runs]);

  // Split into active/history based on current filter
  const activeRuns = useMemo(
    () => filtered.filter((r) => r.status === 'running' || r.status === 'queued' || r.status === 'paused'),
    [filtered],
  );
  const historyRuns = useMemo(
    () => filtered.filter((r) => r.status === 'completed' || r.status === 'failed'),
    [filtered],
  );

  const showActive = statusFilter === 'all' || statusFilter === 'running' || statusFilter === 'queued' || statusFilter === 'paused';
  const showHistory = statusFilter === 'all' || statusFilter === 'completed' || statusFilter === 'failed';

  const headerSx = {
    color: 'rgba(255,255,255,0.5)',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    '& .MuiTableSortLabel-root': {
      color: 'rgba(255,255,255,0.5)',
      '&:hover': { color: 'rgba(255,255,255,0.8)' },
      '&.Mui-active': { color: '#fff' },
    },
    '& .MuiTableSortLabel-icon': { color: 'rgba(255,255,255,0.3) !important' },
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h3" sx={{ mb: 0.5, fontWeight: 600 }}>
            Training Runs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {runs.length} run{runs.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/runs/new')}
          sx={{ backgroundColor: '#EF8997', '&:hover': { backgroundColor: '#e5707f' } }}
        >
          New Run
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 12, gap: 2 }}>
          <CircularProgress sx={{ color: '#EF8997' }} />
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)' }}>
            Loading runs...
          </Typography>
        </Box>
      ) : runs.length === 0 ? (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255,255,255,0.12)',
          }}
        >
          <SmartToyOutlinedIcon sx={{ fontSize: 48, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No training runs yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first run to start training
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate('/runs/new')}
            sx={{ borderColor: '#EF8997', color: '#EF8997' }}
          >
            New Run
          </Button>
        </Paper>
      ) : (
        <>
          {/* Toolbar: search + status filter pills */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search runs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'rgba(255,255,255,0.3)', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 260,
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255,255,255,0.04)',
                  '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                  '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                  '&.Mui-focused fieldset': { borderColor: '#EF8997' },
                },
                '& input': { color: '#fff', fontSize: 13 },
              }}
            />
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {STATUS_FILTERS.map((s) => {
                const active = statusFilter === s;
                const count = statusCounts[s] || 0;
                if (s !== 'all' && count === 0) return null;
                return (
                  <Box
                    key={s}
                    onClick={() => setStatusFilter(s)}
                    sx={{
                      cursor: 'pointer',
                      px: 1.5,
                      py: 0.5,
                      borderRadius: 1,
                      fontSize: 12,
                      fontWeight: 500,
                      color: active ? '#fff' : 'rgba(255,255,255,0.5)',
                      backgroundColor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
                      transition: 'all 0.15s',
                      userSelect: 'none',
                    }}
                  >
                    {s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
                    {' '}
                    <Box component="span" sx={{ color: 'rgba(255,255,255,0.35)', ml: 0.5 }}>
                      {count}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {/* Active Runs — Card Layout */}
          {showActive && activeRuns.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, display: 'block' }}>
                Active
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {activeRuns.map((run) => {
                  const envLabel = getEnvLabel(run);
                  const isRunning = run.status === 'running';
                  const color = getStatusColor(run.status);
                  const ms = Date.now() - new Date(run.created_at).getTime();
                  const h = Math.floor(ms / 3600000);
                  const m = Math.floor((ms % 3600000) / 60000);
                  const elapsed = h > 0 ? `${h}h ${m}m` : `${m}m`;

                  return (
                    <Paper
                      key={run.id}
                      onClick={() => navigate(`/runs/${run.slug}`)}
                      sx={{
                        p: 2.5,
                        flex: '1 1 280px',
                        maxWidth: 400,
                        cursor: 'pointer',
                        backgroundColor: '#0a0a0a',
                        border: '1px solid rgba(255,255,255,0.08)',
                        transition: 'border-color 0.2s, transform 0.15s',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.2)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: color,
                            flexShrink: 0,
                            ...(isRunning && { animation: 'pulse 2s infinite ease-in-out' }),
                          }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {run.name}
                        </Typography>
                      </Box>
                      {envLabel && (
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block', mb: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {envLabel}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          {isRunning ? 'Training...' : 'Queued'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.35)' }}>
                          {elapsed}
                        </Typography>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* History — Compact Table */}
          {showHistory && historyRuns.length > 0 && (
            <Box>
              {showActive && activeRuns.length > 0 && (
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', mb: 1.5, display: 'block' }}>
                  History
                </Typography>
              )}
              <Box
                sx={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  backgroundColor: '#0a0a0a',
                  overflow: 'hidden',
                }}
              >
                {/* Table header */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '2.5fr 90px 90px 90px 110px 40px',
                    gap: 2,
                    px: 2.5,
                    py: 1.5,
                    borderBottom: '1px solid rgba(255,255,255,0.08)',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    ...headerSx,
                  }}
                >
                  <Box>
                    <TableSortLabel
                      active={sortField === 'name'}
                      direction={sortField === 'name' ? sortDir : 'asc'}
                      onClick={() => handleSort('name')}
                    >
                      Name
                    </TableSortLabel>
                  </Box>
                  <Box>
                    <TableSortLabel
                      active={sortField === 'status'}
                      direction={sortField === 'status' ? sortDir : 'asc'}
                      onClick={() => handleSort('status')}
                    >
                      Status
                    </TableSortLabel>
                  </Box>
                  <Box>
                    <TableSortLabel
                      active={sortField === 'duration'}
                      direction={sortField === 'duration' ? sortDir : 'asc'}
                      onClick={() => handleSort('duration')}
                    >
                      Duration
                    </TableSortLabel>
                  </Box>
                  <Box>
                    <TableSortLabel
                      active={sortField === 'cost'}
                      direction={sortField === 'cost' ? sortDir : 'asc'}
                      onClick={() => handleSort('cost')}
                    >
                      Est. Cost
                    </TableSortLabel>
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <TableSortLabel
                      active={sortField === 'created_at'}
                      direction={sortField === 'created_at' ? sortDir : 'asc'}
                      onClick={() => handleSort('created_at')}
                    >
                      Date
                    </TableSortLabel>
                  </Box>
                  <Box />
                </Box>

                {/* Rows */}
                {historyRuns.map((run) => {
                  const color = getStatusColor(run.status);
                  const durationMs = getDurationMs(run);
                  const cost = getEstCost(run);

                  return (
                    <Box
                      key={run.id}
                      onClick={() => navigate(`/runs/${run.slug}`)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '2.5fr 90px 90px 90px 110px 40px',
                        gap: 2,
                        px: 2.5,
                        py: 1.5,
                        cursor: 'pointer',
                        alignItems: 'center',
                        borderBottom: '1px solid rgba(255,255,255,0.04)',
                        '&:hover': { backgroundColor: 'rgba(255,255,255,0.03)', '& .delete-btn': { opacity: 1 } },
                        transition: 'background-color 0.15s',
                        '&:last-child': { borderBottom: 'none' },
                      }}
                    >
                      {/* Name */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: '#fff',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {run.name}
                        </Typography>
                        {run.wandb_url && (
                          <Box
                            component="a"
                            href={run.wandb_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            sx={{
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              opacity: 0.4,
                              '&:hover': { opacity: 1 },
                              transition: 'opacity 0.15s',
                            }}
                          >
                            <img src="/wandb-logo.png" alt="W&B" style={{ width: 14, height: 14 }} />
                          </Box>
                        )}
                      </Box>

                      {/* Status */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box
                          sx={{
                            width: 7,
                            height: 7,
                            borderRadius: '50%',
                            backgroundColor: color,
                            flexShrink: 0,
                          }}
                        />
                        <Typography variant="caption" sx={{ color, fontWeight: 500, fontSize: 12 }}>
                          {run.status.charAt(0).toUpperCase() + run.status.slice(1)}
                        </Typography>
                      </Box>

                      {/* Duration */}
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                        {formatDuration(durationMs)}
                      </Typography>

                      {/* Est. Cost */}
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
                        ${cost.toFixed(2)}
                      </Typography>

                      {/* Date */}
                      <Typography
                        variant="caption"
                        sx={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, textAlign: 'right' }}
                      >
                        {relativeTime(run.created_at)}
                      </Typography>

                      {/* Delete */}
                      <Box
                        className="delete-btn"
                        onClick={(e: React.MouseEvent) => handleDeleteClick(run, e)}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: deleting === run.slug ? 0.3 : 0,
                          transition: 'opacity 0.15s',
                          cursor: 'pointer',
                        }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16, color: 'rgba(255,255,255,0.4)', '&:hover': { color: '#ff6b6b' } }} />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}

          {/* No results after filtering */}
          {filtered.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="body2" color="text.secondary">
                No runs match your filters
              </Typography>
            </Box>
          )}
        </>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteClose}
        PaperProps={{
          sx: {
            backgroundColor: '#0a0a0a',
            border: '1px solid rgba(255,255,255,0.12)',
            borderRadius: 2,
            minWidth: 440,
          },
        }}
      >
        <DialogTitle
          sx={{
            color: '#fff',
            fontWeight: 600,
            fontSize: 18,
            pb: 1,
          }}
        >
          Delete run
        </DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {deleteTarget && (deleteTarget.status === 'running' || deleteTarget.status === 'queued') && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                backgroundColor: 'rgba(255, 152, 0, 0.08)',
                border: '1px solid rgba(255, 152, 0, 0.3)',
                color: 'rgba(255,255,255,0.9)',
                '& .MuiAlert-icon': { color: '#ffa726' },
              }}
            >
              This run is currently active. Deleting it will stop all training and remove all artifacts.
            </Alert>
          )}
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
            This action cannot be undone. To confirm, type the run name{' '}
            <Box
              component="span"
              sx={{ color: '#fff', fontWeight: 600, fontFamily: 'monospace', fontSize: 13 }}
            >
              {deleteTarget?.name}
            </Box>{' '}
            below.
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder={deleteTarget?.name || ''}
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            autoFocus
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(255,255,255,0.04)',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.12)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.24)' },
                '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.4)' },
              },
              '& input': { color: '#fff', fontSize: 13, fontFamily: 'monospace' },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5, gap: 1 }}>
          <Button
            onClick={handleDeleteClose}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.06)' },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            disabled={deleteConfirmText !== deleteTarget?.name}
            variant="contained"
            color="error"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              '&.Mui-disabled': {
                backgroundColor: 'rgba(211, 47, 47, 0.2)',
                color: 'rgba(255,255,255,0.3)',
              },
            }}
          >
            Delete run
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RunsList;
